// Dashboard — pharmacy home. Compliance-and-service KPIs + volume
// trend + this-week table, modeled on CNC's HQ dashboard shell
// (~/cnc-driver/screens/HomeScreen.js, dispatch app) but with content
// swapped for pharmacy priorities (follow-ups instead of driver
// leaderboard, on-time rate instead of top-driver-per-day).
//
// v0 preview: layout is real, numbers are static placeholders. Wired
// to real aggregations in a follow-up PR. The `v0 preview` banner at
// the top makes that explicit so nobody mistakes the placeholders for
// actual pharmacy signal.
//
// Deferred to later PRs (in rough priority):
//   - Aggregation RPC (`dashboard_summary`) — returns KPI counts +
//     14-day volume trend + follow-up reasons breakdown + this-week
//     rows in one round trip. Called from a TanStack Query hook here.
//   - Range filter (Last 7 / 30 / 90 days / MTD / Custom) actually
//     drives the aggregation instead of just re-rendering placeholders.
//     Range tabs mirror the /reports page for cross-page consistency —
//     both share the same date-window vocabulary.
//   - Interactive drill-through — click a day in Volume Trend to jump
//     to /orders?date=…, click a follow-up reason to filter /exceptions.
//   - Per-location breakdown for multi-pharmacy tenants.
//   - Recent activity feed panel (10 most recent state changes).

import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { requireUserType } from '../lib/auth/guards';
import { Card } from '../components/ui/Card';
import { useSetPageHeader } from '../components/layout/PageHeaderContext';

export const Route = createFileRoute('/_authenticated/dashboard')({
  // Pharmacy-only. Dispatchers land on /dispatch; platform_admin
  // passes through via the LYN-staff override inside requireUserType.
  beforeLoad: async () => {
    await requireUserType({ allowed: ['pharmacy_admin', 'pharmacy_staff'] });
  },
  component: DashboardPage,
});

function DashboardPage(): JSX.Element {
  useSetPageHeader({
    title: 'Dashboard',
    subtitle: 'This week at a glance.',
  });
  return <DashboardView />;
}

// ----------------------------------------------------------------------------
// Placeholder data — swap for real aggregation query in follow-up PR.

// Range vocabulary matches /reports (see _authenticated.reports.tsx
// RangeKey/RANGES). Keeping the two pages in lockstep so a pharmacy
// admin has one mental model for "date window" across the portal.
type RangeKey = '7d' | '30d' | '90d' | 'mtd' | 'custom';

const RANGES: Array<{ key: RangeKey; label: string }> = [
  { key: '7d', label: 'Last 7 days' },
  { key: '30d', label: 'Last 30 days' },
  { key: '90d', label: 'Last 90 days' },
  { key: 'mtd', label: 'Month to date' },
  { key: 'custom', label: 'Custom' },
];

const PLACEHOLDER_KPIS = {
  thisWeek: { value: 187, delta: '+12% vs last week', deltaTone: 'up' as const },
  avgPerDay: { value: 26, sub: 'Last 30 delivery days' },
  openFollowUps: { value: 3, delta: '-2 vs yesterday', deltaTone: 'down' as const },
};

// 14 days of volume — most recent last. Values mirror CNC HQ's shape
// so the visual weight is realistic. Days computed relative to today
// so the label row reads naturally regardless of when you open this.
const PLACEHOLDER_VOLUME: Array<{ label: string; count: number }> = [
  { label: 'Mon', count: 22 },
  { label: 'Tue', count: 31 },
  { label: 'Wed', count: 28 },
  { label: 'Thu', count: 34 },
  { label: 'Fri', count: 41 },
  { label: 'Sat', count: 12 },
  { label: 'Sun', count: 8 },
  { label: 'Mon', count: 26 },
  { label: 'Tue', count: 33 },
  { label: 'Wed', count: 29 },
  { label: 'Thu', count: 37 },
  { label: 'Fri', count: 44 },
  { label: 'Sat', count: 14 },
  { label: 'Sun', count: 9 },
];

const PLACEHOLDER_REASONS: Array<{ label: string; count: number }> = [
  { label: 'Returned', count: 18 },
  { label: 'Missing POD', count: 7 },
  { label: 'Aged pending', count: 4 },
  { label: 'Cold-chain deviation', count: 2 },
];

const PLACEHOLDER_WEEK: Array<{
  day: string;
  date: string;
  stops: number;
  cold: number;
  failed: number;
  onTime: number;
  status: 'complete' | 'in_progress';
}> = [
  { day: 'Mon', date: 'Aug 11', stops: 32, cold: 4, failed: 1, onTime: 94, status: 'complete' },
  { day: 'Tue', date: 'Aug 12', stops: 28, cold: 6, failed: 0, onTime: 96, status: 'complete' },
  { day: 'Wed', date: 'Aug 13', stops: 35, cold: 5, failed: 2, onTime: 88, status: 'complete' },
  { day: 'Thu', date: 'Aug 14', stops: 41, cold: 3, failed: 1, onTime: 92, status: 'complete' },
  { day: 'Fri', date: 'Aug 15', stops: 44, cold: 5, failed: 0, onTime: 97, status: 'complete' },
  { day: 'Mon', date: 'Aug 17', stops: 26, cold: 4, failed: 1, onTime: 95, status: 'in_progress' },
];

// ----------------------------------------------------------------------------

function DashboardView(): JSX.Element {
  const [range, setRange] = useState<RangeKey>('30d');
  return (
    <div className="space-y-6">
      <PreviewBanner />

      <RangeTabs value={range} onChange={setRange} />

      <KpiRow />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <VolumeTrendCard />
        </div>
        <ReasonsBreakdownCard />
      </div>

      <ThisWeekTable />
    </div>
  );
}

// ----------------------------------------------------------------------------

function PreviewBanner(): JSX.Element {
  return (
    <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
      <span className="font-semibold">v0 preview</span> — layout is
      live; numbers are placeholders until the aggregation RPC lands.
      Nothing on this page reflects real deliveries yet.
    </div>
  );
}

// Same visual + label set as /reports RangeTabs for parity. When we
// wire real aggregation, both pages should share a single component
// in apps/web/src/components/ui/ — extraction deferred until the
// data layer swap so we don't churn markup twice.
function RangeTabs({
  value,
  onChange,
}: {
  value: RangeKey;
  onChange: (v: RangeKey) => void;
}): JSX.Element {
  return (
    <div className="inline-flex rounded-lg border border-border bg-bg-soft p-0.5">
      {RANGES.map((r) => {
        const active = value === r.key;
        return (
          <button
            key={r.key}
            type="button"
            onClick={() => onChange(r.key)}
            className={
              active
                ? 'rounded-md bg-bg px-3 py-1 text-xs font-medium text-ink shadow-sm'
                : 'rounded-md px-3 py-1 text-xs text-ink-2 hover:text-ink'
            }
          >
            {r.label}
          </button>
        );
      })}
    </div>
  );
}

// ----------------------------------------------------------------------------
// KPI row — three big-number cards with delta annotations.

function KpiRow(): JSX.Element {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
      <KpiCard
        label="This week"
        value={PLACEHOLDER_KPIS.thisWeek.value.toLocaleString()}
        delta={PLACEHOLDER_KPIS.thisWeek.delta}
        deltaTone={PLACEHOLDER_KPIS.thisWeek.deltaTone}
      />
      <KpiCard
        label="Avg / day"
        value={PLACEHOLDER_KPIS.avgPerDay.value.toLocaleString()}
        sub={PLACEHOLDER_KPIS.avgPerDay.sub}
      />
      <KpiCard
        label="Open follow-ups"
        value={PLACEHOLDER_KPIS.openFollowUps.value.toLocaleString()}
        delta={PLACEHOLDER_KPIS.openFollowUps.delta}
        deltaTone={PLACEHOLDER_KPIS.openFollowUps.deltaTone}
      />
    </div>
  );
}

function KpiCard({
  label,
  value,
  delta,
  deltaTone,
  sub,
}: {
  label: string;
  value: string;
  delta?: string;
  deltaTone?: 'up' | 'down' | 'neutral';
  sub?: string;
}): JSX.Element {
  const deltaClass =
    deltaTone === 'up'
      ? 'text-emerald-600'
      : deltaTone === 'down'
        ? 'text-red-600'
        : 'text-ink-3';
  return (
    <Card className="!p-4">
      <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-3">
        {label}
      </p>
      <p className="mt-1 text-3xl font-bold tracking-tight text-ink">{value}</p>
      {delta ? <p className={`mt-1 text-xs ${deltaClass}`}>{delta}</p> : null}
      {sub ? <p className="mt-1 text-xs text-ink-3">{sub}</p> : null}
    </Card>
  );
}

// ----------------------------------------------------------------------------
// Volume Trend — inline SVG bar chart, no chart lib. 14 days most-
// recent-last. Each bar labeled with count above + weekday below.
// Simple horizontal reference line at the mean lets you eyeball
// which days ran hot vs cold.

function VolumeTrendCard(): JSX.Element {
  const data = PLACEHOLDER_VOLUME;
  const max = Math.max(...data.map((d) => d.count));
  const avg = Math.round(data.reduce((s, d) => s + d.count, 0) / data.length);
  const chartHeight = 140;
  const barWidth = 32;
  const barGap = 8;
  const chartWidth = data.length * (barWidth + barGap);
  const avgY = chartHeight - (avg / max) * chartHeight;
  return (
    <Card className="!p-5">
      <div className="mb-4 flex items-baseline justify-between">
        <h2 className="text-sm font-semibold text-ink">Volume trend</h2>
        <p className="text-xs text-ink-3">Last 14 days · avg {avg}/day</p>
      </div>
      <div className="overflow-x-auto">
        <svg
          width={chartWidth}
          height={chartHeight + 40}
          role="img"
          aria-label="Delivery volume over the last 14 days"
        >
          {/* Reference line at the mean. */}
          <line
            x1={0}
            x2={chartWidth}
            y1={avgY}
            y2={avgY}
            stroke="#cbd5e1"
            strokeDasharray="3 4"
            strokeWidth={1}
          />
          {data.map((d, i) => {
            const barHeight = (d.count / max) * chartHeight;
            const x = i * (barWidth + barGap);
            const y = chartHeight - barHeight;
            return (
              <g key={i}>
                <rect
                  x={x}
                  y={y}
                  width={barWidth}
                  height={barHeight}
                  rx={3}
                  fill="#60a5fa"
                />
                <text
                  x={x + barWidth / 2}
                  y={y - 4}
                  textAnchor="middle"
                  fontSize={9}
                  fill="#64748b"
                  fontWeight={600}
                >
                  {d.count}
                </text>
                <text
                  x={x + barWidth / 2}
                  y={chartHeight + 14}
                  textAnchor="middle"
                  fontSize={10}
                  fill="#94a3b8"
                >
                  {d.label}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    </Card>
  );
}

// ----------------------------------------------------------------------------
// Follow-up reasons — right-rail breakdown. Bar length is proportional
// so the eye jumps to the top offender. Clicking a row would filter
// /exceptions by bucket in v2.

function ReasonsBreakdownCard(): JSX.Element {
  const data = PLACEHOLDER_REASONS;
  const max = Math.max(...data.map((d) => d.count));
  return (
    <Card className="!p-5">
      <div className="mb-4 flex items-baseline justify-between">
        <h2 className="text-sm font-semibold text-ink">
          Follow-up reasons
        </h2>
        <p className="text-xs text-ink-3">Last 30 days</p>
      </div>
      <ul className="space-y-2.5">
        {data.map((row) => {
          const pct = (row.count / max) * 100;
          return (
            <li key={row.label}>
              <div className="mb-1 flex items-baseline justify-between">
                <span className="text-xs text-ink">{row.label}</span>
                <span className="text-xs font-semibold text-ink-2">
                  {row.count}
                </span>
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-bg-soft">
                <div
                  className="h-full rounded-full bg-primary"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

// ----------------------------------------------------------------------------
// This week table — day-by-day recent history. Mirrors CNC HQ's
// "This Week's Dispatches" panel but with pharmacy-appropriate
// columns: cold-chain count and on-time % replace top-driver.

function ThisWeekTable(): JSX.Element {
  return (
    <Card className="!p-5">
      <h2 className="mb-4 text-sm font-semibold text-ink">
        This week's deliveries
      </h2>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs">
          <thead>
            <tr className="border-b border-border">
              {['Day', 'Date', 'Stops', 'Cold', 'Failed', 'On-time', 'Status'].map(
                (h) => (
                  <th
                    key={h}
                    className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-ink-3"
                  >
                    {h}
                  </th>
                ),
              )}
            </tr>
          </thead>
          <tbody>
            {PLACEHOLDER_WEEK.map((row) => (
              <tr
                key={row.date}
                className="border-b border-border last:border-b-0"
              >
                <td className="px-3 py-2.5 font-medium text-ink">{row.day}</td>
                <td className="px-3 py-2.5 text-ink-2">{row.date}</td>
                <td className="px-3 py-2.5 text-ink">{row.stops}</td>
                <td className="px-3 py-2.5 text-ink-2">{row.cold}</td>
                <td className="px-3 py-2.5 text-ink-2">
                  {row.failed === 0 ? '—' : row.failed}
                </td>
                <td className="px-3 py-2.5 text-ink">{row.onTime}%</td>
                <td className="px-3 py-2.5">
                  <span
                    className={
                      row.status === 'complete'
                        ? 'inline-block rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700'
                        : 'inline-block rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700'
                    }
                  >
                    {row.status === 'complete' ? 'Complete' : 'In progress'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
